import { useEffect } from "react";

/**
 * Lightweight client-side SEO hook.
 * Updates <title>, <meta name="description">, canonical link and Open Graph tags on mount.
 * Optionally injects a JSON-LD structured data block.
 */
// Default robots value for public, indexable pages.
const DEFAULT_ROBOTS = "index, follow, max-image-preview:large";

export function useSeo({ title, description, canonical, ogImage, jsonLd, hreflangs, lang, robots } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevLang = document.documentElement.getAttribute("lang");
    if (title) document.title = title;
    if (lang) document.documentElement.setAttribute("lang", lang);

    const upsertMeta = (selector, attrs) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement(attrs.tagName || "meta");
        Object.entries(attrs.create).forEach(([k, v]) => tag.setAttribute(k, v));
        document.head.appendChild(tag);
      }
      Object.entries(attrs.set).forEach(([k, v]) => tag.setAttribute(k, v));
      return tag;
    };

    // Always enforce a robots tag (defensive — guarantees Googlebot sees the right directive
    // after client-side rendering, even if a script or extension altered the head).
    const prevRobots = document.head.querySelector('meta[name="robots"]')?.getAttribute("content") || null;
    const robotsValue = robots || DEFAULT_ROBOTS;
    upsertMeta('meta[name="robots"]', {
      create: { name: "robots" }, set: { content: robotsValue },
    });
    // Mirror to googlebot for extra-specific control.
    upsertMeta('meta[name="googlebot"]', {
      create: { name: "googlebot" }, set: { content: robotsValue },
    });

    if (description) {
      upsertMeta('meta[name="description"]', {
        create: { name: "description" }, set: { content: description },
      });
      upsertMeta('meta[property="og:description"]', {
        create: { property: "og:description" }, set: { content: description },
      });
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', {
        create: { property: "og:title" }, set: { content: title },
      });
      upsertMeta('meta[name="twitter:title"]', {
        create: { name: "twitter:title" }, set: { content: title },
      });
    }
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      upsertMeta('meta[property="og:url"]', {
        create: { property: "og:url" }, set: { content: canonical },
      });
    }
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', {
        create: { property: "og:image" }, set: { content: ogImage },
      });
      upsertMeta('meta[name="twitter:image"]', {
        create: { name: "twitter:image" }, set: { content: ogImage },
      });
    }

    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.text = JSON.stringify(jsonLd);
      jsonLdEl.dataset.seoHook = "1";
      document.head.appendChild(jsonLdEl);
    }
    // Inject hreflang alternate links (and remove on cleanup)
    const hreflangEls = [];
    if (Array.isArray(hreflangs)) {
      hreflangs.forEach((h) => {
        if (!h?.lang || !h?.url) return;
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", h.lang);
        el.setAttribute("href", h.url);
        el.dataset.seoHook = "1";
        document.head.appendChild(el);
        hreflangEls.push(el);
      });
    }
    return () => {
      document.title = prevTitle;
      if (prevLang) document.documentElement.setAttribute("lang", prevLang);
      if (jsonLdEl) jsonLdEl.remove();
      hreflangEls.forEach((el) => el.remove());
      // Reset robots to default (index, follow) — never leave a noindex behind
      // when navigating from a private page back to a public one.
      const robotsTag = document.head.querySelector('meta[name="robots"]');
      if (robotsTag) robotsTag.setAttribute("content", prevRobots || DEFAULT_ROBOTS);
      const googlebotTag = document.head.querySelector('meta[name="googlebot"]');
      if (googlebotTag) googlebotTag.setAttribute("content", prevRobots || DEFAULT_ROBOTS);
    };
  }, [title, description, canonical, ogImage, jsonLd, hreflangs, lang, robots]);
}


/**
 * Convenience hook for private/authenticated pages.
 * Marks the current page as `noindex, nofollow` for Google.
 * Restores the default `index, follow` on unmount.
 */
export function useNoIndex() {
  useSeo({ robots: "noindex, nofollow" });
}
